import type { Message } from "@/src/features/api/NotifyApi";

interface NotifyElement {
  type: string;
  props?: Record<string, unknown>;
  children?: string[];
}

interface NotifyRenderSpec {
  root: string;
  elements: Record<string, NotifyElement>;
}

const NOW = Date.now();
const ONE_HOUR_MS = 60 * 60 * 1000;

const createStructuredNotificationContent = (
  title: string,
  body: NotifyRenderSpec,
): string => {
  return JSON.stringify({
    title,
    body: `<dynamic_ui>${JSON.stringify(body)}</dynamic_ui>`,
  });
};

export const NOTIFY_MESSAGE_MOCK_ROWS: Message[] = [
  {
    id: "mock-notify-1",
    business_type: "PROACTIVE_STEWARD",
    title: "Trending Stocks",
    params: JSON.stringify({
      source: "mock",
      campaign: "notification-debug",
    }),
    receiver: "mock-owner-1",
    created: NOW - ONE_HOUR_MS,
    content: createStructuredNotificationContent("Trending Stocks", {
      root: "mock-notification-card-1",
      elements: {
        "mock-notification-card-1": {
          type: "NotificationCardContainer",
          props: {
            label: "Ed's Picks for You",
            title: "Trending Stocks",
            generatedAtMs: NOW - ONE_HOUR_MS,
          },
          children: [
            "mock-notification-text-1",
            "mock-asset-recommendation-list-1",
            "mock-notice-actions-1",
          ],
        },
        "mock-notification-text-1": {
          type: "NotificationText",
          props: {
            text: "Ed highlighted several large-cap names with strong growth momentum and resilient pricing power.",
          },
          children: [],
        },
        "mock-asset-recommendation-list-1": {
          type: "AssetRecommendationList",
          props: {
            sectionLabel: "Theme",
            assets: [
              {
                assetLogo:
                  "https://www.figma.com/api/mcp/asset/c25c9a9c-3737-42dd-a753-446c63e0d811",
                assetSymbol: "NVDA",
                assetPriceUsd: 171.23,
                priceChange24h: 0.61,
                reason: "Growth beats 95% of sector over the last 4 quarters",
                assetScoreValue: 88,
                assetScoreTotal: 100,
                type: "stock",
              },
              {
                assetLogo:
                  "https://www.figma.com/api/mcp/asset/68bdbd5e-f85e-4b45-9a5e-7bddaf3075b2",
                assetSymbol: "TSLA",
                assetPriceUsd: 171.23,
                priceChange24h: 0.61,
                reason: "AI and energy tailwinds continue to support the setup",
                assetScoreValue: 86,
                assetScoreTotal: 100,
                type: "stock",
              },
            ],
          },
          children: [],
        },
        "mock-notice-actions-1": {
          type: "NotificationButtonList",
          props: {
            actions: [
              {
                type: "negative",
                attributes: {
                  catId: "skip",
                  label: "Not interested",
                },
              },
              {
                type: "positive",
                attributes: {
                  catId: "ask_ed",
                  label: "Ask Ed",
                  question: "Compare NVDA vs AMD fundamentals",
                },
              },
            ],
          },
          children: [],
        },
      },
    }),
  },
  {
    id: "mock-notify-2",
    business_type: "PROACTIVE_STEWARD",
    title: "My Pick This Week: TSM",
    params: JSON.stringify({
      source: "mock",
      campaign: "notification-debug",
    }),
    receiver: "mock-owner-1",
    created: NOW - 2 * ONE_HOUR_MS,
    content: createStructuredNotificationContent("My Pick This Week: TSM", {
      root: "mock-notification-card-2",
      elements: {
        "mock-notification-card-2": {
          type: "NotificationCardContainer",
          props: {
            label: "Alpha Picks",
            title: "My Pick This Week: TSM",
            generatedAtMs: NOW - 2 * ONE_HOUR_MS,
            className: "bg-background_solid_primary",
          },
          children: [
            "mock-notification-text-2",
            "mock-asset-header-2",
            "mock-asset-dimension-score-table-2",
            "mock-asset-narrative-highlights-list-2",
          ],
        },
        "mock-notification-text-2": {
          type: "NotificationText",
          props: {
            text: "TSM remains one of the strongest foundry names as advanced node demand compounds with AI infrastructure growth.",
          },
          children: [],
        },
        "mock-asset-header-2": {
          type: "AssetHeader",
          props: {
            assetLogo:
              "https://www.figma.com/api/mcp/asset/c25c9a9c-3737-42dd-a753-446c63e0d811",
            assetSymbol: "TSM",
            title: "Taiwan Semiconductor",
            assetScoreValue: 91,
            assetScoreTotal: 100,
            type: "stock",
          },
          children: [],
        },
        "mock-asset-dimension-score-table-2": {
          type: "AssetDimensionScoreTable",
          props: {
            sectionLabel: "Dimension Score",
            rows: [
              {
                dimensionName: "Growth",
                scoreValue: 92,
                scoreTotal: 100,
                rankValue: 3,
                rankTotalAssets: 100,
              },
              {
                dimensionName: "Profitability",
                scoreValue: 88,
                scoreTotal: 100,
                rankValue: 5,
                rankTotalAssets: 100,
              },
            ],
          },
          children: [],
        },
        "mock-asset-narrative-highlights-list-2": {
          type: "AssetNarrativeHighlightsList",
          props: {
            items: [
              {
                title: "Advanced node revenue mix keeps improving",
                description:
                  "Higher leading-edge mix strengthens pricing power and margin durability.",
              },
              {
                title: "AI demand supports multi-quarter visibility",
                description:
                  "Capacity expansion continues to be backed by structural AI infrastructure demand.",
              },
            ],
          },
          children: [],
        },
      },
    }),
  },
];
